<script lang="ts">
const borderSize = 0.0625; /* 1px */
const basePadding = 0.25; /* 4px between each icon (9px before (8px + 1px border) and 8px after the icons if any) */
/** CSS anchor positioning support (absent in Safari) — evaluated once. */
const anchorSupported =
  typeof CSS !== 'undefined' && !!CSS.supports?.('top', 'anchor(--probe center)');
const inputFontSize = 0.875; /* var(--nxt-font-size-sm) */

type InputPaddingInline = `calc(${number}em / ${typeof inputFontSize})`;

type InlineIcon = {
  htmlTag: 'button' | 'span';
  onClick?: (payload: PointerEvent) => void;
  tooltip?: string;
  label?: string;
  isAction?: boolean;
  reveals?: boolean;
  props?: {
    disabled?: boolean;
    style?: Omit<
      CSSProperties,
      'inset-inline-start' | 'insetInlineStart' | 'inset-inline-end' | 'insetInlineEnd'
    >;
  };
  iconComponent: Component;
  iconProps: {
    filled: boolean;
    size: `${number}em`;
    color: string;
    style?: CSSProperties;
  };
};
type InlineStartIcon = InlineIcon & {
  props: { style: { 'inset-inline-start'?: never; insetInlineStart: `${number}em` } };
};
</script>

<script setup lang="ts">
import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useAttrs,
  useId,
  watch,
  watchEffect,
  type Component,
  type CSSProperties,
  type HTMLAttributes,
} from 'vue';
import {
  FONT_SIZE_LOOKUP,
  inputActionsAlignInjectionKey,
  inputPlainCellInjectionKey,
  isInTableCell,
  labelInjectionKey,
  loadingStateInjectionKey,
  type BaseTextInputProps,
} from './internal';
import {
  classifyIcons,
  decoIconsOccupyPx,
  hostWantsStartActions,
  isTrailingCompact,
  mirrorActionOrder,
  reservedTrailingPaddingPx,
  shouldShowTrailingDivider,
  splitActionsForWidth,
  TRAILING_ACTION_GAP_PX,
  TRAILING_ACTION_PX,
  trailingAlwaysVisiblePx,
} from './composables/inputTrailingZone';
import { computeAriaBindings } from './composables/inputSaveStatus';
import { useLoadingStateRegistration } from './composables/useLoadingStateRegistration';
import { default as NxLoadingWrapperIcon } from './NxLoadingWrapperIcon.vue';
import NxInputActionCluster from './NxInputActionCluster.vue';
import NxSaveStateBadge from './NxSaveStateBadge.vue';
import { NxTooltip } from '../Tooltip';
import { useDesign } from '../theme/design';

const props = defineProps<
  BaseTextInputProps & {
    multiline?: boolean;
    /**
     * Reserve the measured action rail inside a plain table cell instead of
     * overlaying lookup/goto/clear on a long textual value.
     */
    reservePlainCellActionSpace?: boolean;
    /**
     * The value is not valid (a validation finding, not a save status): the
     * field takes the error border and its control `aria-invalid="true"`.
     */
    invalid?: boolean;
  } & /* @vue-ignore */ Omit<HTMLAttributes, 'value'>
>();

let elementId = props.id;
// Attempt to retrieve the label injection (it is expected to be missing)
const labelInjection = inject(labelInjectionKey, null);
// Only run the following block if the label injection is available and no element got associated with it yet
if (labelInjection && !labelInjection.for.value) {
  // If no element ID is given, an unique identifier is generated here and assigned to the id attribute
  elementId ??= useId();
  // The element ID is sent trough the injection to the dependency provider → the label gets associated with the input
  labelInjection.setFor(elementId);
}

// Disable the default fallthrough attributes behavior. The fallthrough attributes always have to be assigned to the input even if the icon wrapper is the root element
defineOptions({ inheritAttrs: false });

const loadingState = inject(loadingStateInjectionKey, null);
useLoadingStateRegistration(loadingState);

const attrs = useAttrs();
const status = computed(() => loadingState?.status.value ?? null);
const inputDisabled = computed(() => {
  return !!(status.value?.disabled ?? attrs.disabled);
});

// Status descriptors for the reserved status slot.
const statusState = computed(() => status.value?.state ?? 'idle');
const statusFadeOut = computed(() => status.value?.fadeOut ?? false);
const statusErrorMessage = computed(() => status.value?.errorMessage);
const successPresentation = computed(() => loadingState?.successPresentation ?? 'icon');
const errorPresentation = computed(() => loadingState?.errorPresentation ?? 'icon');
const paintsSuccessBorder = computed(
  () => statusState.value === 'success' && successPresentation.value === 'border'
);
const paintsErrorBorder = computed(
  () => statusState.value === 'error' && errorPresentation.value === 'border'
);

// App-wide `reveal` design axis. `hover` hides functional icons until the field
// is hovered/focused. It is ORed with the per-icon `revealOnInteraction` flag
// (either enables reveal) — `||`, not `??`, because the per-icon flag defaults
// to a defined `false` and would swallow the axis under nullish-coalescing.
const design = useDesign();
const axisReveal = computed(() => design.reveal === 'hover');
// Whether a trailing action icon reveals on interaction (per-icon opt-in OR axis).
const actionReveals = (icon: { revealOnInteraction?: boolean }): boolean =>
  !!icon.revealOnInteraction || axisReveal.value;

// Trailing (align:'end') icons, split into non-clickable deco and clickable actions.
const endIcons = computed(() => (props.icons ?? []).filter((i) => i.align === 'end'));
const endDeco = computed(() => classifyIcons(endIcons.value).deco);
const endActions = computed(() => classifyIcons(endIcons.value).actions);
// True when every trailing action reveals on interaction — i.e. nothing on the
// action side is visible at rest. Lets the divider hide alongside them so it
// doesn't float next to invisible icons until the field is hovered/focused.
const endActionsAllReveal = computed(
  () => endActions.value.length > 0 && endActions.value.every(actionReveals)
);
const injectedActionsAlign = inject(inputActionsAlignInjectionKey, null);
const injectedPlainCell = inject(inputPlainCellInjectionKey, null);
const closestAlignEnd = ref(false);
const inGhostCell = ref(false);
/** In an NxTable cell the action icons stay Tab stops; in a form Tab skips them. */
const inTableCell = ref(false);

const actionsAtStart = computed(() =>
  hostWantsStartActions({
    prop: props.actionsAlign,
    injected: injectedActionsAlign?.value ?? null,
    closestAlignEnd: closestAlignEnd.value,
  })
);
/** Spreadsheet-like table body edit: no input chrome, icons overlay the cell. */
const isPlainCell = computed(() => injectedPlainCell?.value === true || inGhostCell.value);

// App-wide `savestatus` design axis. `badge` moves the save status out of the
// trailing zone onto a corner badge, so an idle field reserves no width and a
// starting save shifts nothing. Table cells stay on the slot in both values:
// their trailing zone is already an overlay, and the cell cursor owns the
// corner. Like `reveal`, this axis is read here rather than left to the cascade
// — it swaps markup, not just style.
const usesBadge = computed(() => design.savestatus === 'badge' && !isPlainCell.value);
/** A border-presented success/error is the whole feedback; a badge on top would
 *  say the same thing twice. */
const badgeState = computed(() =>
  paintsSuccessBorder.value || paintsErrorBorder.value ? 'idle' : statusState.value
);

/** Same rule as the template: deco, or a loading-wrapper status slot (even idle).
 *  Hidden when actions sit on the opposite edge (right-aligned table cells). */
const showTrailingDivider = computed(
  () =>
    !actionsAtStart.value &&
    shouldShowTrailingDivider(
      endActions.value.length,
      endDeco.value.length,
      loadingState != null && !usesBadge.value
    )
);

// Wrapper root — used to move focus onto the field's editable element when the
// overflow affordance (more-icon) is clicked, so `:focus-within` expands the actions.
const wrapperRootEl = ref<HTMLElement | null>(null);

// Exposed so a field built on this wrapper can hand "the whole field" to logic
// that needs it (click-outside, scroll-into-view) instead of finding it back by
// class name, which fails silently when the markup moves.
defineExpose({ wrapperElement: wrapperRootEl });

// Width available to the inline action sub-zone. Measured from the wrapper; the rest
// of the trailing group (deco + reserved status slot + divider) is subtracted.
// These px constants are first-guesses to be tuned in the browser (B7).
const PER_ACTION_PX = TRAILING_ACTION_PX;
const TRIGGER_PX = TRAILING_ACTION_PX;
const PLAIN_ACTION_RAIL_INSET_PX = 4;
const availableActionPx = ref<number>(Number.POSITIVE_INFINITY);

// The overflow affordance (surplus actions collapsed behind a "more" trigger) is
// a rest-state feature: the trigger sits visible while idle and swaps for the
// actions on focus. That directly contradicts reveal-on-interaction icons, which
// are meant to be hidden at rest — a visible "more" trigger would defeat the
// clean idle look. So overflow is only used when NO action reveals; as soon as
// one does (per-icon flag or the `reveal` axis), every action stays inline and
// reveals together on hover/focus.
const actionSplit = computed(() =>
  endActions.value.some(actionReveals)
    ? { inline: [...endActions.value], overflow: [] }
    : splitActionsForWidth(endActions.value, availableActionPx.value, PER_ACTION_PX, TRIGGER_PX)
);
/** Leading zone: reverse so lookup stays next to the value (`X … 19 %`). */
const startActionSplit = computed(() => ({
  inline: mirrorActionOrder(actionSplit.value.inline),
  overflow: mirrorActionOrder(actionSplit.value.overflow),
}));

/**
 * Focus the field's editable element so `:focus-within` expands the trailing
 * actions. The editable element is the slot content (a direct child of the
 * wrapper): a real <input>/<textarea>, or a focusable node inside a
 * `.fake-input`. The lookup is scoped to direct children / `.fake-input` so the
 * leading and trailing icon buttons (also focusable) are never picked.
 */
function focusInput(): void {
  const root = wrapperRootEl.value;
  if (!root) return;
  const el =
    root.querySelector<HTMLElement>(':scope > input, :scope > textarea') ??
    root.querySelector<HTMLElement>(':scope > .fake-input [tabindex]:not([tabindex="-1"])') ??
    root.querySelector<HTMLElement>(':scope > .fake-input');
  el?.focus();
}

const anchorName = `--anchor-${useId()}`;

const inlineStartIcons = shallowRef<InlineStartIcon[]>([]);
const paddingInlineStart = ref<InputPaddingInline | undefined>();

// Trailing group is anchored to the input's right edge. Padding-inline-end is
// computed from deco / busy-status / occupying actions / divider — never from
// offsetWidth of a zero-width idle status slot.
const trailingGroupEl = ref<HTMLElement | null>(null);
const endZonePaddingPx = ref<number>(0);
/** Lookup/goto pad on the inline-start edge (right-aligned table cells). */
const startActionPadPx = ref<number>(0);
/** Trailing lookup/goto stay visible; columns use a min-width so values fit. */
const trailingCompact = ref(false);
let trailingResizeObserver: ResizeObserver | null = null;
let actionZoneResizeObserver: ResizeObserver | null = null;
function syncHostContext(wrapperEl: HTMLElement | null) {
  closestAlignEnd.value = !!wrapperEl?.closest(
    '[data-nx-actions-align="start"], .td.align-end, .th.align-end'
  );
  inGhostCell.value = !!wrapperEl?.closest('.nx-ghost-cell');
  inTableCell.value = isInTableCell(wrapperEl);
}
function syncTrailingMetrics(wrapperEl: HTMLElement | null) {
  const wrapperWidth = wrapperEl?.clientWidth ?? 0;
  // A corner badge is out of flow, so a busy field must not reserve padding for
  // it either — that reservation is what makes the value jump on every save.
  const statusBusy =
    !usesBadge.value &&
    statusState.value !== 'idle' &&
    !paintsSuccessBorder.value &&
    !paintsErrorBorder.value;
  const decoPx = decoIconsOccupyPx(endDeco.value);
  const alwaysVisiblePx = trailingAlwaysVisiblePx(decoPx, statusBusy);
  const compact = isTrailingCompact(wrapperWidth, alwaysVisiblePx, endActions.value.length);
  trailingCompact.value = compact;
  if (wrapperEl) {
    const reserved = alwaysVisiblePx + 12;
    availableActionPx.value = Math.max(0, Math.floor(wrapperWidth * 0.5) - reserved);
  }
  // Reveal icons keep their flex box (`visibility: hidden`), so they occupy
  // width even at rest. Pad for every occupying action plus the divider —
  // otherwise "Kilogramm" runs through the hairline in front of the clear X.
  const occupyingActionCount =
    actionSplit.value.inline.length + (actionSplit.value.overflow.length > 0 ? 1 : 0);
  if (isPlainCell.value) {
    // Numeric cells keep lookup/goto/clear as transient overlays so their
    // value edge matches display mode. Long textual hero cells can opt into a
    // measured action rail: the value then ellipsizes before the controls
    // instead of painting underneath them. Save status stays an overlay.
    const reserveActions = props.reservePlainCellActionSpace === true;
    startActionPadPx.value = 0;
    endZonePaddingPx.value =
      reservedTrailingPaddingPx({
        wrapperWidthPx: wrapperWidth,
        decoCount: endDeco.value.length,
        decoPx,
        occupyingActionCount: reserveActions ? occupyingActionCount : 0,
        actionGapPx: reserveActions ? TRAILING_ACTION_GAP_PX : 0,
        statusBusy: false,
        compact,
        showDivider: false,
      }) + (reserveActions && occupyingActionCount > 0 ? PLAIN_ACTION_RAIL_INSET_PX : 0);
    return;
  }
  if (actionsAtStart.value) {
    // Right-aligned values sit against padding-inline-end; start pad only
    // keeps long numbers off the left-hand lookup/goto (form fields, not ghost).
    startActionPadPx.value = occupyingActionCount * TRAILING_ACTION_PX;
    endZonePaddingPx.value = reservedTrailingPaddingPx({
      wrapperWidthPx: wrapperWidth,
      decoCount: endDeco.value.length,
      decoPx,
      occupyingActionCount: 0,
      statusBusy,
      compact,
      showDivider: false,
    });
    return;
  }
  startActionPadPx.value = 0;
  endZonePaddingPx.value = reservedTrailingPaddingPx({
    wrapperWidthPx: wrapperWidth,
    decoCount: endDeco.value.length,
    decoPx,
    occupyingActionCount,
    statusBusy,
    compact,
    showDivider: showTrailingDivider.value,
  });
}

onMounted(() => {
  const wrapperEl = wrapperRootEl.value ?? trailingGroupEl.value?.parentElement ?? null;
  syncHostContext(wrapperEl);
  if (!trailingGroupEl.value) return;
  trailingResizeObserver = new ResizeObserver(() => syncTrailingMetrics(wrapperEl));
  trailingResizeObserver.observe(trailingGroupEl.value);

  if (wrapperEl) {
    actionZoneResizeObserver = new ResizeObserver(() => syncTrailingMetrics(wrapperEl));
    actionZoneResizeObserver.observe(wrapperEl);
    syncTrailingMetrics(wrapperEl);
  }
});
onBeforeUnmount(() => {
  trailingResizeObserver?.disconnect();
  actionZoneResizeObserver?.disconnect();
});

watch(
  [
    statusState,
    successPresentation,
    errorPresentation,
    endDeco,
    endActions,
    actionsAtStart,
    isPlainCell,
  ],
  () => {
    const wrapperEl = wrapperRootEl.value ?? trailingGroupEl.value?.parentElement ?? null;
    if (wrapperEl) syncTrailingMetrics(wrapperEl);
  }
);

// Keydown handling: bind the consumer's `onKeydown` on the wrapper div
// rather than letting it fall through to the inner input only. This way
// keydowns from focusable icon-buttons inside the wrapper also reach the
// listener (needed for NxTable's arrow/Tab navigation when focus sits on
// an inline icon). Strip `onKeydown` from `inputAttrs` so it doesn't
// double-fire on the input.
const resolvedPaddingInlineStart = computed(() => {
  const iconPad = paddingInlineStart.value;
  const actionPad = isPlainCell.value ? 0 : startActionPadPx.value;
  if (actionPad > 0 && iconPad) return `calc(${iconPad} + ${actionPad}px)`;
  if (actionPad > 0) return `${actionPad}px`;
  if (iconPad) return iconPad;
  return isPlainCell.value ? '0px' : undefined;
});

const resolvedPaddingInlineEnd = computed(() => {
  if (isPlainCell.value || actionsAtStart.value) {
    // Match the display cell's right edge: deco (%, €, CHF) only.
    return endZonePaddingPx.value ? `calc(${endZonePaddingPx.value}px + 0.125em)` : '0px';
  }
  return endZonePaddingPx.value
    ? `calc(${endZonePaddingPx.value}px + 0.375em)`
    : 'var(--nxt-space-sm)';
});

const iconZoneFontSize = computed(() =>
  isPlainCell.value ? '1em' : `calc(${FONT_SIZE_LOOKUP[props.height ?? 's']} / 0.875)`
);

const inputAttrs = computed(() => {
  const { onKeydown: _stripKeydown, ...rest } = attrs as Record<string, unknown>;
  return {
    autocomplete: 'off',
    spellcheck: false,
    ...rest,
    ...computeAriaBindings(statusState.value),
    ...(props.invalid ? { 'aria-invalid': 'true' as const } : {}),
    id: elementId,
    disabled: inputDisabled.value,
    style: {
      ...(attrs.style ? attrs.style : undefined),
      paddingInlineStart: resolvedPaddingInlineStart.value,
      paddingInlineEnd: resolvedPaddingInlineEnd.value,
      ...(isPlainCell.value ? { paddingBlock: '0px' } : {}),
      anchorName,
    },
  };
});

function onWrapperKeydown(event: KeyboardEvent) {
  const fn = (attrs as Record<string, unknown>).onKeydown;
  if (typeof fn === 'function') {
    (fn as (e: KeyboardEvent) => void)(event);
  }
}

watchEffect(() => {
  const tmpInlineStartIcons: InlineStartIcon[] = [];
  let insetInlineStart = basePadding + borderSize;
  let outermostInsetStart: number | undefined;

  for (const inlineIconProps of (props.icons ?? []).filter((i) => i.align === 'start')) {
    if (!inlineIconProps) continue;

    if (inlineIconProps.size && !inlineIconProps.size.endsWith('em')) {
      throw new Error(
        `Invalid value for inline icon size given: ${JSON.stringify(inlineIconProps.size)} (does not end with "em")`
      );
    }
    const inlineIconSize = Number((inlineIconProps.size ?? '1.125em').split('em')[0]);
    if (Number.isNaN(inlineIconSize)) {
      throw new Error(
        `Invalid value for inline icon size given: ${JSON.stringify(inlineIconProps.size)} (not numeric)`
      );
    }

    // Same deco/action split as the trailing zone: an icon is an action when it
    // is clickable (or forced via `deco: false`). Actions default to the brand
    // color and honor reveal-on-interaction; deco icons stay neutral and are
    // always shown. Overflow stays trailing-only.
    const isAction = !(inlineIconProps.deco ?? inlineIconProps.onClick == null);
    const reveals = isAction && actionReveals(inlineIconProps);

    const inlineIcon: InlineIcon = {
      htmlTag: inlineIconProps.onClick ? 'button' : 'span',
      props: { disabled: inputDisabled.value },
      onClick: !inputDisabled.value ? inlineIconProps.onClick : undefined,
      tooltip: inlineIconProps.tooltip,
      label: inlineIconProps.label,
      isAction,
      reveals,
      iconComponent: inlineIconProps.component,
      iconProps: {
        filled: inlineIconProps.filled ?? false,
        size: `${inlineIconSize}em`,
        color:
          inlineIconProps.color ?? (isAction ? 'var(--nxt-brand-bg)' : 'var(--nxt-border-strong)'),
      },
    };

    insetInlineStart += basePadding;
    if (outermostInsetStart === undefined) outermostInsetStart = insetInlineStart;
    tmpInlineStartIcons.push({
      ...inlineIcon,
      props: {
        ...inlineIcon.props,
        style: {
          ...inlineIcon.props?.style,
          insetInlineStart: `${insetInlineStart}em`,
          top: props.multiline
            ? `calc(anchor(${anchorName} top) + ${outermostInsetStart + inlineIconSize / 2}em)`
            : `anchor(${anchorName} center)`,
        },
      },
    });
    insetInlineStart += inlineIconSize;
  }

  if (insetInlineStart === basePadding + borderSize) {
    paddingInlineStart.value = undefined;
  } else {
    insetInlineStart += basePadding * 2;
    paddingInlineStart.value = `calc(${insetInlineStart}em / ${inputFontSize})`;
  }

  inlineStartIcons.value = tmpInlineStartIcons;
});
</script>

<template>
  <div
    ref="wrapperRootEl"
    class="input-wrapper"
    data-focus-group
    :data-status="statusState"
    :data-invalid="props.invalid ? 'true' : undefined"
    :data-disabled="inputDisabled || undefined"
    :data-trailing-compact="trailingCompact ? 'true' : undefined"
    :data-actions-align="actionsAtStart ? 'start' : undefined"
    :data-plain-cell="isPlainCell ? 'true' : undefined"
    :data-reserved-action-rail="
      isPlainCell && props.reservePlainCellActionSpace ? 'true' : undefined
    "
    :style="[
      props.wrapperStyle,
      {
        '--nxt-plain-action-size': `${TRAILING_ACTION_PX}px`,
        '--nxt-plain-action-gap': `${TRAILING_ACTION_GAP_PX}px`,
        '--nxt-plain-action-inset': `${PLAIN_ACTION_RAIL_INSET_PX}px`,
      },
    ]"
    @keydown="onWrapperKeydown"
  >
    <template
      v-for="(inlineIcon, inlineStartIconIndex) in inlineStartIcons"
      :key="`inline_start_icon_${inlineStartIconIndex + 1}`"
    >
      <!-- With a tooltip: NxTooltip is the positioned trigger (so the floating
           panel anchors over the icon, not a zero-size wrapper); the icon button
           sits static inside it. Without one: the icon positions itself as before. -->
      <NxTooltip
        v-if="inlineIcon.tooltip && !isPlainCell"
        :text="inlineIcon.tooltip"
        :style="{
          position: 'absolute',
          top: inlineIcon.props.style.top,
          left: `calc(anchor(${anchorName} left) + ${inlineIcon.props.style.insetInlineStart})`,
          transform: 'translateY(-50%)',
          'font-size': `calc(${FONT_SIZE_LOOKUP[props.height ?? 's']} / 0.875)`,
        }"
      >
        <component
          :is="inlineIcon.htmlTag"
          class="inline-icon is-leading"
          :class="{ action: inlineIcon.isAction, reveal: inlineIcon.reveals }"
          v-bind="inlineIcon.props"
          :style="{ position: 'static', transform: 'none' }"
          :aria-label="inlineIcon.label"
          @mousedown="
            inlineIcon.onClick ? ($event.preventDefault(), $event.stopPropagation()) : undefined
          "
          @click="inlineIcon.onClick"
          @keydown.enter.space.stop="() => {}"
        >
          <component :is="inlineIcon.iconComponent" v-bind="inlineIcon.iconProps" />
        </component>
      </NxTooltip>
      <component
        :is="inlineIcon.htmlTag"
        v-else
        class="inline-icon is-leading"
        :class="{ action: inlineIcon.isAction, reveal: inlineIcon.reveals }"
        v-bind="inlineIcon.props"
        :style="{
          position: 'absolute',
          top: inlineIcon.props.style.top,
          left: `calc(anchor(${anchorName} left) + ${inlineIcon.props.style.insetInlineStart})`,
          'font-size': `calc(${FONT_SIZE_LOOKUP[props.height ?? 's']} / 0.875)`,
        }"
        :aria-label="inlineIcon.label"
        @mousedown="
          inlineIcon.onClick ? ($event.preventDefault(), $event.stopPropagation()) : undefined
        "
        @click="inlineIcon.onClick"
        @keydown.enter.space.stop="() => {}"
      >
        <component :is="inlineIcon.iconComponent" v-bind="inlineIcon.iconProps" />
      </component>
    </template>
    <slot v-bind="{ inputAttrs, textHidden: status?.textHidden }"></slot>
    <div
      v-if="actionsAtStart && endActions.length > 0"
      class="leading-action-zone"
      :data-plain-action-rail="isPlainCell && props.reservePlainCellActionSpace ? '' : undefined"
      :style="{
        position: 'absolute',
        top: anchorSupported
          ? props.multiline
            ? `anchor(${anchorName} top)`
            : `anchor(${anchorName} center)`
          : props.multiline
            ? '0'
            : '50%',
        left: anchorSupported
          ? `calc(anchor(${anchorName} left) + ${basePadding + borderSize}em)`
          : `${basePadding + borderSize}em`,
        transform: props.multiline ? `translateY(${basePadding}em)` : 'translateY(-50%)',
        'font-size': iconZoneFontSize,
      }"
    >
      <NxInputActionCluster
        :inline="startActionSplit.inline"
        :overflow="startActionSplit.overflow"
        :input-disabled="inputDisabled"
        :reveals="actionReveals"
        :show-tooltips="!isPlainCell"
        :form-field="!inTableCell"
        @focus-input="focusInput"
      />
    </div>
    <div
      ref="trailingGroupEl"
      class="trailing-zone"
      :data-multiline="props.multiline ? '' : undefined"
      :data-plain-action-rail="
        isPlainCell && props.reservePlainCellActionSpace && !actionsAtStart && endActions.length > 0
          ? ''
          : undefined
      "
      :style="{
        position: 'absolute',
        // Anchored to the input element where CSS anchor positioning exists.
        // WITHOUT support (Safari) an anchor() value is invalid, the whole
        // declaration drops, and the absolute zone falls to its STATIC spot —
        // directly behind the input text, painting over the last characters.
        // The fallback anchors to the wrapper (position:relative, full width)
        // instead: right edge of the field area rather than of the input box.
        top: anchorSupported
          ? props.multiline
            ? `anchor(${anchorName} top)`
            : `anchor(${anchorName} center)`
          : props.multiline
            ? '0'
            : '50%',
        right: anchorSupported
          ? isPlainCell
            ? `anchor(${anchorName} right)`
            : `calc(anchor(${anchorName} right) + ${basePadding + borderSize}em)`
          : isPlainCell
            ? '0'
            : `${basePadding + borderSize}em`,
        transform: props.multiline ? `translateY(${basePadding}em)` : 'translateY(-50%)',
        'font-size': iconZoneFontSize,
      }"
    >
      <!-- Order: status (loading/error) → deco (€, %, …) → divider → actions.
           Deco must render once only — a second loop left currency cells as
           "6,90 € €" after a merge re-introduced the pre-move placement.
           Right-aligned table cells keep deco here and move actions to
           `.leading-action-zone` so the value shares the display right edge. -->
      <span
        v-if="loadingState && !usesBadge"
        class="status-slot"
        :data-state="statusState"
        :data-success-presentation="successPresentation"
        :data-error-presentation="errorPresentation"
      >
        <NxTooltip
          v-if="statusState === 'error' && !paintsErrorBorder"
          :text="statusErrorMessage ?? ''"
          side="top"
          :style="statusErrorMessage ? 'pointer-events: auto' : ''"
        >
          <NxLoadingWrapperIcon :state="statusState" :fade-out="false" fade-out-duration="500ms" />
        </NxTooltip>
        <NxLoadingWrapperIcon
          v-else-if="statusState !== 'idle' && !paintsSuccessBorder && !paintsErrorBorder"
          :state="statusState"
          :fade-out="statusFadeOut"
          fade-out-duration="500ms"
        />
      </span>

      <span v-for="(icon, i) in endDeco" :key="`deco-${i}`" class="inline-icon deco">
        <component
          :is="icon.component"
          :filled="icon.filled ?? false"
          :size="icon.size ?? '1.125em'"
          :color="icon.color ?? 'var(--nxt-border-strong)'"
        />
      </span>

      <span
        v-if="showTrailingDivider"
        class="trailing-divider"
        :class="{ reveal: endActionsAllReveal }"
        aria-hidden="true"
      />

      <NxInputActionCluster
        v-if="!actionsAtStart"
        :inline="actionSplit.inline"
        :overflow="actionSplit.overflow"
        :input-disabled="inputDisabled"
        :reveals="actionReveals"
        :show-tooltips="!isPlainCell"
        :form-field="!inTableCell"
        @focus-input="focusInput"
      />
    </div>

    <!-- `savestatus: badge` — the status leaves the trailing row for the field's
         top-right corner, at the same offsets the wrapper-less controls use
         (NxCheckbox, NxSwitch, NxNativeSelect), so a field and a checkbox next
         to it carry their save status in the same place. -->
    <NxSaveStateBadge
      v-if="loadingState && usesBadge"
      class="save-badge"
      :state="badgeState"
      :error-message="statusErrorMessage"
      :fade-out="statusFadeOut"
    />
  </div>
</template>

<style scoped src="./style.css" />
<style scoped>
/* The icon wrapper is unique to the input for now, therefore it is not included in the generic stylesheet */
.input-wrapper {
  position: relative;
  width: 100%;

  .inline-icon {
    position: absolute;
    top: 50%;
    border-radius: var(--nxt-radius-sm);
    transform: translateY(-50%);
    display: flex;
  }

  span.inline-icon {
    pointer-events: none;
  }

  button.inline-icon {
    border: none;
    font-size: var(--nxt-font-size-md);
    outline: none;
    padding: 0;
    background: none;
    cursor: pointer;

    &:enabled {
      transition: background-color var(--nxt-motion-fast);

      &:hover {
        background-color: var(--nxt-surface-sunken-bg);
      }

      &:focus {
        outline: var(--nxt-hairline) solid var(--nxt-focus-ring);
      }
    }

    &:disabled {
      pointer-events: none;
    }
  }

  /* Input icon colour is driven by the `inputicon` design axis (design.css):
     rest = --nxd-input-icon-rest, hover = --nxd-input-icon-hover, focus = brand
     (the focus-within block below). Default (active) keeps icons neutral until
     the field is focused; `brand` colours them brand throughout. The !important
     overrides the per-icon `color` prop the same way the focus-within rule does. */
  .inline-icon:deep(svg) {
    color: var(--nxd-input-icon-rest) !important;
  }
  /* "Disabled" is read off the DOM in three places, and each is the only source
     for its family: real inputs carry `:disabled` (NxInput spreads `$attrs` onto
     the element and hands the wrapper only `props`, so `attrs.disabled` never
     arrives); fake inputs are divs that can never match `:disabled`, so consumers
     mark the div; the injected loading status only ever reaches this root.
     Direct child and element-restricted on purpose — a bare `:has(:disabled)`
     would also catch a consumer's own disabled control inside the field.
     Both rules need the full condition: `:not()` adds its argument's weight, so
     an unqualified hover rule outranks the disabled rule below. */
  &:hover:not(:focus-within):not([data-disabled]):not(
      :has(> :is(input, textarea):disabled, > .fake-input[data-disabled])
    )
    .inline-icon:deep(svg) {
    color: var(--nxd-input-icon-hover) !important;
  }
  /* A disabled field mutes its icons the way it mutes its text
     (--nxt-input-fg-disabled in style.css), instead of holding the full resting
     colour while text and border go grey. */
  &[data-disabled] .inline-icon:deep(svg),
  &:has(> :is(input, textarea):disabled, > .fake-input[data-disabled]) .inline-icon:deep(svg) {
    color: var(--nxt-input-fg-disabled) !important;
  }

  &:focus-within:deep() {
    :deep(.fake-input),
    :deep(input) {
      outline: 0;
      border: var(--nxt-hairline) solid var(--nxt-input-border-focus); /* 1px */
      box-shadow: 0 0 0 calc(0.125em / 0.875) var(--nxt-input-ring); /* 2px */
      transition: box-shadow var(--nxt-motion-fast);
    }

    .inline-icon:deep(svg) {
      color: var(--nxt-brand-bg) !important;
    }
  }

  /* Status border colors — override the focus border so the state stays visible while focused. */
  &[data-status='success']:deep() {
    :deep(.fake-input),
    :deep(input) {
      border-color: var(--nxt-success-bg);
    }
  }
  /* A textarea takes the error border only (I-2813): a refused value must show
     at the field; a green frame on a long text was not asked for. */
  &[data-status='error']:deep() {
    :deep(.fake-input),
    :deep(input),
    :deep(textarea) {
      border-color: var(--nxt-input-border-invalid);
    }
  }
  /* `invalid`: the same error border for a value that fails validation, with no
     save behind it — input, textarea and .fake-input alike. */
  &[data-invalid]:deep() {
    :deep(.fake-input),
    :deep(input),
    :deep(textarea) {
      border-color: var(--nxt-input-border-invalid);
    }
  }

  /* Plain table body cells: no focus ring — the table cursor is the chrome. */
  &[data-plain-cell]:focus-within:deep() {
    :deep(.fake-input),
    :deep(input) {
      border: none;
      box-shadow: none;
    }
  }
}

.trailing-zone,
.leading-action-zone {
  display: flex;
  align-items: center;
  gap: 0.125em; /* 2px */
  pointer-events: none; /* children re-enable */
}

/* Opted-in textual spreadsheet cells reserve lookup/goto/clear beside the
   value. Give those controls a real action rail instead of painting three
   bare glyphs directly onto the last characters. The larger targets remain
   comfortable in both 36px and 48px rows, and only the hovered/focused action
   takes the brand colour.

   NO background and NO separator of its own. Both were the rail's own paint and
   both were wrong at rest:

   - the background came from `--nxt-table--row-active-bg`, which NxTable defines
     on the TABLE root rather than per row — so every row got the ACTIVE row's
     fill inside its action rail, a visibly different block on the n-1 rows that
     are not active. It protected nothing either: the zone is a flex item in flow
     (its icons are `position: static`), so no value text can run underneath it.
   - the separator was an inset box-shadow, which cannot reveal. It therefore sat
     there as a lone border-left next to INVISIBLE icons on every row nobody was
     pointing at.

   The separator every other input uses is the real `.trailing-divider` element,
   and it already reveals in lockstep with the icons when the trailing actions
   are all reveal-on-interaction (`endActionsAllReveal`) — which is how the sales
   positions grid behaves. The reserved rail used to hide that element and draw
   its own; now it keeps it, so a plain cell separates its actions exactly the
   way the rest of the app does, and shows nothing at rest. */
.input-wrapper[data-plain-cell] [data-plain-action-rail] {
  gap: var(--nxt-plain-action-gap);
  padding-inline-start: var(--nxt-plain-action-inset);
}
.input-wrapper[data-plain-cell] [data-plain-action-rail] .status-slot[data-state='idle'] {
  display: none;
}
.input-wrapper[data-plain-cell] [data-plain-action-rail] :deep(button.inline-icon.action) {
  inline-size: var(--nxt-plain-action-size);
  block-size: var(--nxt-plain-action-size);
  align-items: center;
  justify-content: center;
}
.input-wrapper[data-plain-cell] [data-plain-action-rail] :deep(.inline-icon.action svg) {
  color: var(--nxt-fg-muted) !important;
}
.input-wrapper[data-plain-cell]
  [data-plain-action-rail]
  :deep(button.inline-icon.action:is(:hover, :focus-visible) svg) {
  color: var(--nxt-brand-bg) !important;
}
/* Action/deco icons flow in the flex row; reset the absolute positioning the
   leading-icon rule (.input-wrapper .inline-icon) would otherwise apply.
   :deep because the action cluster is a child SFC. */
.trailing-zone :deep(.inline-icon),
.leading-action-zone :deep(.inline-icon) {
  position: static;
  top: auto;
  transform: none;
}
/* Tooltip wrappers around actions stay flex items (no layout jump). */
.trailing-zone :deep(.tooltip-wrapper),
.leading-action-zone :deep(.tooltip-wrapper) {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}
/* Only real (button) actions capture pointer events; a span-action (no onClick,
   e.g. a select chevron) stays click-through so the field handles the click. */
.trailing-zone :deep(button.inline-icon.action),
.leading-action-zone :deep(button.inline-icon.action) {
  pointer-events: auto;
}
/* --- Reveal-on-interaction (per-icon opt-in via `revealOnInteraction`) ---
   A functional icon hidden until hover/focus. It keeps its flex box (visibility,
   not width) on WIDE fields so revealing does not shift padding. Narrow fields
   use compact mode instead: actions unmount / display:none and padding is
   computed from deco + busy status only — never from this reserved box. */
.trailing-zone :deep(.inline-icon.action.reveal),
.leading-action-zone :deep(.inline-icon.action.reveal) {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.12s ease,
    visibility 0s linear 0.12s;
}
.input-wrapper:hover .trailing-zone :deep(.inline-icon.action.reveal),
.input-wrapper:focus-within .trailing-zone :deep(.inline-icon.action.reveal),
.input-wrapper:hover .leading-action-zone :deep(.inline-icon.action.reveal),
.input-wrapper:focus-within .leading-action-zone :deep(.inline-icon.action.reveal) {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s;
}
/* Leading icons mirror the trailing action behavior: a reveal-on-interaction
   leading action is hidden until the field is hovered or focused. Its space is
   held by `padding-inline-start` regardless of visibility, so revealing it never
   shifts the text (same guarantee as the trailing zone). Scoped via `.is-leading`
   so it never collides with the trailing-zone rules above. */
.input-wrapper .inline-icon.is-leading.action.reveal {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.12s ease,
    visibility 0s linear 0.12s;
}
.input-wrapper:hover .inline-icon.is-leading.action.reveal,
.input-wrapper:focus-within .inline-icon.is-leading.action.reveal {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s;
}
/* When the only trailing actions are reveal icons, the divider would otherwise
   float next to invisible icons at rest — reveal it in lockstep with them. Its
   box stays reserved (visibility, not width), so padding does not shift. */
.trailing-zone .trailing-divider.reveal {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.12s ease,
    visibility 0s linear 0.12s;
}
.input-wrapper:hover .trailing-zone .trailing-divider.reveal,
.input-wrapper:focus-within .trailing-zone .trailing-divider.reveal {
  opacity: 1;
  visibility: visible;
  transition-delay: 0s;
}
/* Compact no longer hides lookup/goto/clear — column min-widths keep the
   value readable next to the trailing `…`. Padding still reserves occupying
   action + divider width so the value cannot run through the hairline. */
/* --- Trailing-action overflow: focus-driven swap (replaces the dropdown) ---
   The surplus actions and the more-icon animate between a collapsed state
   (zero-width, faded, non-interactive, out of tab order via visibility:hidden)
   and an expanded state. Idle = more-icon visible, surplus collapsed.
   :focus-within = surplus expanded, more-icon collapsed. The visibility
   transition-delay defers hiding until the fade finishes; expanding sets the
   delay to 0 so the icons appear immediately. The trailing-zone ResizeObserver
   turns the width change into padding-inline-end automatically. */
.trailing-zone :deep(.inline-icon.action.surplus),
.trailing-zone :deep(.overflow-trigger),
.leading-action-zone :deep(.inline-icon.action.surplus),
.leading-action-zone :deep(.overflow-trigger) {
  overflow: hidden;
  pointer-events: none;
  transition:
    max-width 0.12s ease,
    opacity 0.12s ease,
    transform 0.12s ease,
    visibility 0s linear 0.12s;
}
.trailing-zone :deep(.inline-icon.action.surplus),
.leading-action-zone :deep(.inline-icon.action.surplus) {
  max-width: 0;
  opacity: 0;
  transform: translateX(0.25em);
  visibility: hidden;
  pointer-events: none;
}
.trailing-zone :deep(.overflow-trigger),
.leading-action-zone :deep(.overflow-trigger) {
  max-width: 2.5em;
  opacity: 1;
  transform: none;
  visibility: visible;
}
.input-wrapper:focus-within .trailing-zone :deep(.inline-icon.action.surplus),
.input-wrapper:focus-within .leading-action-zone :deep(.inline-icon.action.surplus) {
  max-width: 2.5em;
  opacity: 1;
  transform: none;
  visibility: visible;
  pointer-events: auto;
  transition-delay: 0s;
}
.input-wrapper:focus-within .trailing-zone :deep(.overflow-trigger),
.input-wrapper:focus-within .leading-action-zone :deep(.overflow-trigger) {
  max-width: 0;
  opacity: 0;
  transform: translateX(0.25em);
  visibility: hidden;
  pointer-events: none;
}
.status-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Idle is empty — do not reserve 1.25em in every edit cell (clips "2.02 %"). */
  width: 0;
  min-width: 0;
  overflow: hidden;
  flex: 0 0 auto;
}
.status-slot[data-state='loading'],
.status-slot[data-state='success']:not([data-success-presentation='border']),
.status-slot[data-state='error']:not([data-error-presentation='border']) {
  width: 1.25em;
}
/* Fast table PATCHes should resolve directly into the green cell border instead
   of flashing a one-frame spinner or its circular status background. Slow writes
   still surface after this delay. Keep the slot's width so appearing feedback
   cannot change the trailing-zone geometry. */
.status-slot[data-state='loading'][data-success-presentation='border'] {
  opacity: 0;
  animation: delayed-table-save-indicator 1ms linear 600ms forwards;
}
@keyframes delayed-table-save-indicator {
  to {
    opacity: 1;
  }
}
.input-wrapper[data-plain-cell] .status-slot:not([data-state='idle']) {
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
}
/* `savestatus: badge` — the corner offsets the wrapper-less controls already
   use (NxCheckbox / NxSwitch / NxNativeSelect), so the badge reads as one
   system wherever it appears. Absolute: it never costs the field any width. */
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
.trailing-divider {
  width: 0.0625em; /* 1px hairline */
  align-self: stretch;
  margin-inline: 0.125em;
  background-color: var(--nxt-border);
}
</style>
